import {
  Component,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs';
import { SharedUiModule } from '@stc-apps/shared-ui';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { EUserRole, EUserStatus, UserFilters } from '../models/user.model';
import { EditableDropdownComponent } from '../../../shared/components/editable-dropdown/editable-dropdown.component';
type DropdownOption = { label: string; value: string };
export const ROLE_DISPLAY_MAP: Record<EUserRole, string> = {
  [EUserRole.ApplicationUser]: 'Application User',
  [EUserRole.AppAdmin]: 'Application Admin',
  [EUserRole.ComponentAdmin]: 'Component Admin',
};
export const ROLE_ID_MAP: Record<string, number> = {
  [EUserRole.ApplicationUser]: 1,
  [EUserRole.AppAdmin]: 2,
  [EUserRole.ComponentAdmin]: 3,
};

@Component({
  selector: 'stc-apps-user-management-filters',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    InputIconModule,
    IconFieldModule,
    SharedUiModule,
    EditableDropdownComponent,
  ],
  templateUrl: './user-management-filters.component.html',
  styleUrl: './user-management-filters.component.scss',
})
export class UserManagementFiltersComponent implements OnInit, OnDestroy {
  @Output() filtersChanged = new EventEmitter<UserFilters>();
  filterForm: FormGroup = new FormGroup({});
  private destroy$ = new Subject<void>();
  private fb = inject(FormBuilder);
  categories = input<DropdownOption[]>([]);
  inputPlaceholder = input<string>('');
  enableDropdownFilter = input<boolean>(true);
  // roleOptions: DropdownOption[] = [
  //   { label: 'Application User', value: 'application_user' },
  //   { label: 'Admin', value: 'app_admin' },
  //   { label: 'Components Admin', value: 'components_admin' },
  // ];
  ROLE_OPTIONS = Object.entries(ROLE_DISPLAY_MAP).map(([value, label]) => ({
    value,
    label,
  }));
  statusOptions: DropdownOption[] = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' },
    { label: 'Deactiveted', value: 'DEACTIVATED' },
  ];
  private readonly roleIdMap: Record<string, number> = {
    application_user: 1,
    app_admin: 2,
    components_admin: 3,
  };
  private readonly validStatuses: EUserStatus[] = [
    EUserStatus.ACTIVE,
    EUserStatus.INACTIVE,
    EUserStatus.DEACTIVATED,
  ];

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.filterForm = this.fb.group({
      search: [''],
      role: [''],
      status: [''],
    });
  }

  private setupFormSubscription(): void {
    this.filterForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        map((values) => this.processFormValues(values)),
        takeUntil(this.destroy$)
      )
      .subscribe((values) => this.filtersChanged.emit(values));
  }

  resetFilterControl(controlName: string) {
    this.filterForm.get(controlName)?.reset(null);
    this.filtersChanged.emit(this.filterForm.value);
  }

  resetAllFilters() {
    this.filterForm.reset();
    this.filtersChanged.emit(this.filterForm.value);
  }

  private processFormValues(values: {
    search: string;
    role: DropdownOption | string | null;
    status: DropdownOption | string | null;
  }): UserFilters {
    const role =
      typeof values.role === 'string' ? values.role : values.role?.value || '';
    const status =
      typeof values.status === 'string'
        ? values.status
        : values.status?.value || '';
    if (this.categories().length > 0) {
      return {
        search: values.search,
        role: role || undefined,
        roleId: ROLE_ID_MAP[role],
        status: this.toUserStatus(status),
        historyStatus: status,
      };
    }
    return {
      search: values.search,
      role: role || undefined,
      // roleId: this.roleIdMap[role] ?? undefined,
      roleId: ROLE_ID_MAP[role],
      status: this.toUserStatus(status),
    };
  }
  private toUserStatus(status: string): EUserStatus | undefined {
    return this.validStatuses.includes(status as EUserStatus)
      ? (status as EUserStatus)
      : undefined;
  }
}
